import { useMemo } from 'react'
import { MdChevronLeft, MdChevronRight, MdFirstPage, MdLastPage } from 'react-icons/md'

interface PaginationControlsProps {
    page: number
    limit: number
    total: number
    loading?: boolean
    onPageChange: (nextPage: number) => void
    /** Rótulo no singular ("lead", "oportunidade"). Usado em "X-Y de Z <label>s". */
    label?: string
    /** Quantos números de página mostrar de cada lado da página atual. Default: 1. */
    siblingCount?: number
    /** Quantos números fixos nas pontas (primeira/última). Default: 1. */
    boundaryCount?: number
    /** Esconde os botões "primeira" e "última" página. Default: false. */
    hideEdgeJumps?: boolean
    /** Modo compacto: oculta o texto "Mostrando X-Y" e centraliza os botões. Útil em sidebars estreitas. */
    compact?: boolean
    className?: string
}

const DOTS = 'dots' as const

type PageItem = number | typeof DOTS

function range(start: number, end: number): number[] {
    const length = end - start + 1
    return Array.from({ length }, (_, i) => start + i)
}

function buildPageItems(
    currentPage: number,
    totalPages: number,
    siblingCount: number,
    boundaryCount: number,
): PageItem[] {
    const totalNumbers = boundaryCount * 2 + siblingCount * 2 + 3

    if (totalPages <= totalNumbers) {
        return range(1, totalPages)
    }

    const startPages = range(1, Math.min(boundaryCount, totalPages))
    const endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages)

    const siblingsStart = Math.max(
        Math.min(currentPage - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
        boundaryCount + 2,
    )
    const siblingsEnd = Math.min(
        Math.max(currentPage + siblingCount, boundaryCount + siblingCount * 2 + 2),
        endPages.length > 0 ? endPages[0] - 2 : totalPages - 1,
    )

    const items: PageItem[] = [...startPages]

    if (siblingsStart > boundaryCount + 2) {
        items.push(DOTS)
    } else if (boundaryCount + 1 < totalPages - boundaryCount) {
        items.push(boundaryCount + 1)
    }

    items.push(...range(siblingsStart, siblingsEnd))

    if (siblingsEnd < totalPages - boundaryCount - 1) {
        items.push(DOTS)
    } else if (totalPages - boundaryCount > boundaryCount) {
        items.push(totalPages - boundaryCount)
    }

    items.push(...endPages)

    return items
}

export default function PaginationControls({
    page,
    limit,
    total,
    loading = false,
    onPageChange,
    label,
    siblingCount = 1,
    boundaryCount = 1,
    hideEdgeJumps = false,
    compact = false,
    className = '',
}: PaginationControlsProps) {
    const totalPages = Math.max(1, Math.ceil(total / limit))
    const safePage = Math.min(Math.max(1, page), totalPages)
    const from = total === 0 ? 0 : (safePage - 1) * limit + 1
    const to = Math.min(total, safePage * limit)

    const canPrev = safePage > 1 && !loading
    const canNext = safePage < totalPages && !loading

    const items = useMemo(
        () => buildPageItems(safePage, totalPages, siblingCount, boundaryCount),
        [safePage, totalPages, siblingCount, boundaryCount],
    )

    const suffix = label ? ` ${label}${total === 1 ? '' : 's'}` : ''

    return (
        <div className={`flex items-center ${compact ? 'justify-center' : 'justify-between'} gap-3 flex-wrap py-3 ${className}`}>
            {!compact && (
                <div className="text-xs text-base-content/60">
                    Mostrando <span className="font-medium">{from}-{to}</span> de{' '}
                    <span className="font-medium">{total}</span>
                    {suffix}
                </div>
            )}
            <div className="flex items-center gap-1 flex-wrap">
                {!hideEdgeJumps && (
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => onPageChange(1)}
                        disabled={!canPrev}
                        title="Primeira página"
                        aria-label="Primeira página"
                    >
                        <MdFirstPage className="text-base" />
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => onPageChange(safePage - 1)}
                    disabled={!canPrev}
                    title="Página anterior"
                    aria-label="Página anterior"
                >
                    <MdChevronLeft className="text-base" />
                </button>

                {items.map((item, idx) =>
                    item === DOTS ? (
                        <span
                            key={`dots-${idx}`}
                            className="btn btn-sm btn-ghost pointer-events-none px-2"
                            aria-hidden="true"
                        >
                            …
                        </span>
                    ) : (
                        <button
                            key={item}
                            type="button"
                            className={`btn btn-sm ${item === safePage ? 'btn-secondary' : 'btn-ghost'}`}
                            onClick={() => onPageChange(item)}
                            disabled={loading || item === safePage}
                            aria-label={`Página ${item}`}
                            aria-current={item === safePage ? 'page' : undefined}
                        >
                            {item}
                        </button>
                    ),
                )}

                <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => onPageChange(safePage + 1)}
                    disabled={!canNext}
                    title="Próxima página"
                    aria-label="Próxima página"
                >
                    <MdChevronRight className="text-base" />
                </button>
                {!hideEdgeJumps && (
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => onPageChange(totalPages)}
                        disabled={!canNext}
                        title="Última página"
                        aria-label="Última página"
                    >
                        <MdLastPage className="text-base" />
                    </button>
                )}
            </div>
        </div>
    )
}
